import React, { useEffect, useState, useRef, useCallback } from "react";
import JsSIP from "jssip";
import fetch from "../../utils/fetch";
import Cookies from "../../utils/cookies";

interface SipCredentials {
    sipUser: string;
    sipPass: string;
    sipDomain: string;
    wssUri: string;
}

interface Participant {
    id: string;
    caller_id_name?: string;
    caller_id_number?: string;
    muted?: boolean;
    can_hear?: boolean;
    // Reliable across both call directions — unlike caller_id_number,
    // which does NOT equal the SIP username on inbound calls (confirmed:
    // it showed as a registration-contact-derived string like "ioo27c94"
    // instead). Computed server-side from the channel name prefix.
    is_self?: boolean;
}

// ─── Hard-coded DIDs ───────────────────────────────────────────────────────
const DIDS = [
    { number: "+61280739217", label: "Main Line" },
    { number: "+61290510564", label: "Sales" },
];

const CONFERENCE_API_BASE = "https://siptestea.ypc.solutions/api/conference";
const CONFERENCE_WS_BASE = "wss://siptestea.ypc.solutions/ws/conference";

// ─── Minimal WebPhone ──────────────────────────────────────────────────────
const WebPhone: React.FC<{ credentials: SipCredentials }> = ({ credentials }) => {
    const { sipUser, sipPass, sipDomain, wssUri } = credentials;

    const [reg, setReg] = useState("init"); // init | ok | fail | gone
    const [call, setCall] = useState("idle"); // idle | out | in | active
    const [dialValue, setDialValue] = useState("");
    const [remoteId, setRemoteId] = useState("");
    const [secs, setSecs] = useState(0);

    // ── Conference / call-control state ────────────────────────────────────
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [showParticipants, setShowParticipants] = useState(false);
    const [addNumber, setAddNumber] = useState("");
    const [recording, setRecording] = useState(false);
    const [confBusy, setConfBusy] = useState(false);
    const [confError, setConfError] = useState("");
    // Separate from confBusy: confBusy only covers the HTTP round trip
    // (now near-instant, since /add returns before the dial resolves), but
    // dialingNumber covers the whole "is anyone currently being dialed"
    // window — that's what actually needs to block a second Add click.
    const [dialingNumber, setDialingNumber] = useState<string | null>(null);

    const uaRef = useRef<any>(null);
    const sessRef = useRef<any>(null);
    const audioRef = useRef<HTMLAudioElement>(null);
    // Web Audio context + active tone nodes, used to generate the inbound
    // ringtone and outbound ringback locally in the browser. Generated
    // rather than played from audio files so there's nothing to host,
    // download, or get blocked by autoplay policies mid-call.
    const audioCtxRef = useRef<AudioContext | null>(null);
    const toneStopRef = useRef<(() => void) | null>(null);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const dialTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Mirrors dialingNumber into a ref so the WebSocket's onmessage handler
    // (set up once, not re-created on every state change) always reads the
    // current value instead of a stale one captured at connect time.
    const dialingNumberRef = useRef<string | null>(null);
    useEffect(() => {
        dialingNumberRef.current = dialingNumber;
    }, [dialingNumber]);
    // How many participants existed right before the current Add — the
    // WebSocket message handler compares against this to detect "the new
    // person actually joined" without needing its own polling loop.
    const countBeforeAddRef = useRef(0);

    const wsRef = useRef<WebSocket | null>(null);
    const wsReconnectDelayRef = useRef(1000);
    const wsReconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ── Conference API helper ───────────────────────────────────────────────
    // Authorization/xAccessToken below are just sipUser/sipPass reused —
    // same field names and same identity already used by
    // /user/vca/sip/credentials, just reused here. Uses window.fetch
    // explicitly since the bare `fetch` name is shadowed by the utils/fetch
    // import above; swap this for that utility once its body-passing
    // convention is confirmed, if you'd rather keep everything consistent.
    const confApi = useCallback(
        async (path: string, body: Record<string, any> = {}) => {
            setConfError("");
            try {
                const res = await window.fetch(`${CONFERENCE_API_BASE}${path}`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ Authorization: sipUser, xAccessToken: sipPass, ...body }),
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
                return data;
            } catch (e: any) {
                console.error("[CONF]", path, e.message);
                setConfError(e.message);
                throw e;
            }
        },
        [sipUser, sipPass],
    );

    // Field names (`members`, `caller_id_name`, `muted`, `can_hear`) are
    // confirmed against real `conference <room> list json` output — see
    // server.js's getConferenceMembers for the actual parser.
    const loadParticipants = useCallback(async () => {
        try {
            const data = await confApi("/participants");
            const members = data.members || [];
            setParticipants(members);
            return members;
        } catch {
            /* error already captured in confError */
            return null;
        }
    }, [confApi]);

    const stopDialingWatch = useCallback(() => {
        if (dialTimeoutRef.current) clearTimeout(dialTimeoutRef.current);
        dialTimeoutRef.current = null;
        setDialingNumber(null);
    }, []);

    // Real-time participant updates over WebSocket, replacing what used to
    // be an 8s client-side poll (and, before that, a second poll just for
    // detecting a newly-added participant joining). The server pushes a
    // fresh member list the instant it detects a change — see server.js's
    // conference loop — so this just needs to listen and react, not ask on
    // a timer. Auto-ending the call when everyone else has left is no
    // longer handled here at all: the server kicks the last remaining
    // member directly (confirmed to genuinely end their real call), which
    // produces an actual SIP BYE that the existing sess.on("ended") handler
    // already reacts to — no separate "am I alone" detection needed
    // client-side anymore.
    const connectWs = useCallback(() => {
        const url = `${CONFERENCE_WS_BASE}?Authorization=${encodeURIComponent(sipUser)}&xAccessToken=${encodeURIComponent(sipPass)}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
            console.log("[WS] connected");
            wsReconnectDelayRef.current = 1000; // reset backoff on a healthy connection
        };
        ws.onmessage = (evt) => {
            try {
                const msg = JSON.parse(evt.data);
                if (msg.type === "participants") {
                    const members: Participant[] = msg.members || [];
                    setParticipants(members);
                    if (dialingNumberRef.current && members.length > countBeforeAddRef.current) {
                        stopDialingWatch();
                    }
                } else if (msg.type === "ended") {
                    // Informational only — the actual hangup (if it's this
                    // browser's turn to end) arrives via a real SIP BYE and
                    // the existing sess.on("ended") handler, not from this
                    // message. Logged for visibility during testing.
                    console.log("[WS] server reports the room ended (last member kicked)");
                }
            } catch (e) {
                console.error("[WS] failed to parse message", e);
            }
        };
        ws.onclose = () => {
            console.log("[WS] disconnected — reconnecting");
            wsRef.current = null;
            const delay = wsReconnectDelayRef.current;
            wsReconnectDelayRef.current = Math.min(wsReconnectDelayRef.current * 2, 10_000);
            wsReconnectTimeoutRef.current = setTimeout(connectWs, delay);
        };
        ws.onerror = (e) => console.error("[WS] error", e);
    }, [sipUser, sipPass, stopDialingWatch]);

    const handleAddParticipant = async () => {
        const n = addNumber.trim();
        if (!n || dialingNumber) return; // a dial is already in flight
        setConfBusy(true);
        countBeforeAddRef.current = participants.length;
        try {
            await confApi("/add", { number: n });
            setAddNumber("");
            setShowParticipants(true);
            setDialingNumber(n);
            await loadParticipants(); // shows any existing members right away; the WS push will confirm the new one once they actually answer

            // Safety net in case the WS connection is down or the dial
            // never resolves — mirrors the server's originate_timeout=25
            // plus margin. Normally cleared much sooner by the WS message
            // handler once the new participant actually appears.
            dialTimeoutRef.current = setTimeout(stopDialingWatch, 28000);
        } catch {
            /* surfaced via confError */
        } finally {
            setConfBusy(false);
        }
    };

    const handleMemberAction = async (path: string, memberId: string) => {
        setConfBusy(true);
        try {
            await confApi(path, { memberId });
            await loadParticipants();
        } catch {
            /* surfaced via confError */
        } finally {
            setConfBusy(false);
        }
    };

    const handleToggleRecording = async () => {
        setConfBusy(true);
        try {
            if (recording) {
                await confApi("/record/stop");
                setRecording(false);
            } else {
                await confApi("/record/start");
                setRecording(true);
            }
        } catch {
            /* surfaced via confError */
        } finally {
            setConfBusy(false);
        }
    };

    // ── Call tones ──────────────────────────────────────────────────────
    // Generated with Web Audio rather than played from audio files: nothing
    // to host or download, no autoplay-policy issues mid-call, and the
    // cadence can be defined precisely. Two patterns:
    //   inbound  — the classic two-burst "ring ring ... ring ring" pattern
    //              an agent hears when a call arrives
    //   outbound — the single longer burst an agent hears while the far
    //              end is ringing (ringback), so it's obvious the call is
    //              actually progressing rather than dead air
    const stopTone = useCallback(() => {
        if (toneStopRef.current) {
            toneStopRef.current();
            toneStopRef.current = null;
        }
    }, []);

    const startTone = useCallback(
        (kind: "inbound" | "outbound") => {
            stopTone(); // never layer two tones on top of each other
            try {
                if (!audioCtxRef.current) {
                    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
                    audioCtxRef.current = new Ctx();
                }
                const ctx = audioCtxRef.current;
                if (!ctx) return;
                // Browsers suspend the context until a user gesture; by the
                // time a call exists the agent has clicked something, so
                // this normally resolves immediately.
                if (ctx.state === "suspended") ctx.resume().catch(() => {});

                let cancelled = false;
                const timers: ReturnType<typeof setTimeout>[] = [];

                // One burst of tone: two sine waves (a real telephone ring
                // is two frequencies together, which is why a single tone
                // sounds more like a beep than a ring).
                const burst = (durationSec: number) => {
                    if (cancelled || !ctx) return;
                    const gain = ctx.createGain();
                    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
                    gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02); // soft attack, avoids a click
                    gain.gain.setValueAtTime(0.15, ctx.currentTime + durationSec - 0.02);
                    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durationSec);
                    gain.connect(ctx.destination);
                    for (const freq of [440, 480]) {
                        const osc = ctx.createOscillator();
                        osc.type = "sine";
                        osc.frequency.value = freq;
                        osc.connect(gain);
                        osc.start(ctx.currentTime);
                        osc.stop(ctx.currentTime + durationSec);
                    }
                };

                const cycle = () => {
                    if (cancelled) return;
                    if (kind === "inbound") {
                        // ring-ring, pause — repeats every 3s
                        burst(0.4);
                        timers.push(
                            setTimeout(() => {
                                if (!cancelled) burst(0.4);
                            }, 600),
                        );
                        timers.push(setTimeout(cycle, 3000));
                    } else {
                        // single long burst, longer pause — repeats every 5s
                        burst(1.0);
                        timers.push(setTimeout(cycle, 5000));
                    }
                };
                cycle();

                toneStopRef.current = () => {
                    cancelled = true;
                    // Clear ALL pending timers — the inbound cadence sets
                    // two per cycle, so tracking only the most recent would
                    // let a stray second burst fire ~600ms after the agent
                    // had already answered.
                    for (const t of timers) clearTimeout(t);
                    timers.length = 0;
                };
            } catch (e) {
                console.error("[TONE] failed to start", e);
            }
        },
        [stopTone],
    );

    const attachTrack = useCallback((pc: any) => {
        if (!pc || pc._tracksAttached) return; // guard against double-attaching
        pc._tracksAttached = true;
        console.log("[SIP] attaching ontrack — signalingState=", pc.signalingState);
        pc.ontrack = ({ streams: [s] }: any) => {
            console.log("[SIP] ontrack  stream=", s?.id);
            if (audioRef.current && s) {
                audioRef.current.srcObject = s;
                audioRef.current.play().catch((e: any) => console.error("[SIP] play() failed:", e));
            }
        };
        pc.oniceconnectionstatechange = () => console.log("[SIP] ICE state →", pc.iceConnectionState);
        pc.onconnectionstatechange = () => console.log("[SIP] connection state →", pc.connectionState);
    }, []);

    const wireSession = useCallback(
        (sess: any) => {
            console.log("[SIP] wireSession  connection already exists=", !!sess.connection);
            if (sess.connection) attachTrack(sess.connection);
            sess.on("peerconnection", ({ peerconnection: pc }: any) => {
                console.log("[SIP] peerconnection event fired");
                attachTrack(pc);
            });
            sess.on("connecting", () => console.log("[SIP] connecting"));
            sess.on("progress", (e: any) => {
                console.log("[SIP] progress", e?.response?.status_code);
                // Far end is ringing — play ringback so the agent knows the
                // call is actually progressing rather than sitting silent.
                // Only for outbound: on an inbound call this fires for our
                // own 180 Ringing, where the inbound tone is already playing.
                if (sess.direction === "outgoing") startTone("outbound");
            });
            sess.on("confirmed", async () => {
                console.log("[SIP] confirmed — call active");
                stopTone(); // answered — silence any ringing/ringback
                setCall("active");
                setSecs(0);
                timerRef.current = setInterval(() => setSecs((n) => n + 1), 1000);

                // Promote every call into its conference room immediately,
                // not just once someone's added — this is what makes
                // mute/hold work on a plain 1:1 call. The WebSocket
                // connection (opened once at mount, independent of any
                // single call) will pick up this room's state on its next
                // shared server-side tick; loadParticipants() here just
                // fills that small gap immediately rather than waiting.
                //
                // For a QUEUE-answered call this legitimately has nothing
                // to promote yet (the caller isn't joined for another few
                // seconds) — the server returns ok:true, promoted:false in
                // that case and its own queue loop transfers both legs
                // into the room, so no retry is needed here.
                try {
                    await confApi("/promote");
                    await loadParticipants();
                } catch {
                    /* surfaced via confError; the call itself still proceeds */
                }
            });
            const end = (e?: any) => {
                const cause = e?.cause || e?.message || "unknown";
                const code = e?.response?.status_code || "";
                console.log("[SIP] ended/failed  cause=", cause, " code=", code, e);
                stopTone(); // never leave a tone ringing after the call is gone
                if (timerRef.current) clearInterval(timerRef.current);
                stopDialingWatch();
                setCall("idle");
                setRemoteId("");
                setSecs(0);
                sessRef.current = null;
                if (audioRef.current) audioRef.current.srcObject = null;
                // Conference state is call-scoped — clear it so the next
                // call starts fresh rather than showing stale participants.
                setParticipants([]);
                setShowParticipants(false);
                setRecording(false);
                setConfError("");
            };
            sess.on("ended", end);
            sess.on("failed", end);
        },
        [attachTrack, stopDialingWatch, confApi, loadParticipants, startTone, stopTone],
    );

    useEffect(() => {
        JsSIP.debug.disable("JsSIP:*");
        const ua = new JsSIP.UA({
            sockets: [new JsSIP.WebSocketInterface(wssUri)],
            uri: `sip:${sipUser}@${sipDomain}`,
            password: sipPass,
            register: true,
            session_timers: false,
            // CONFIRMED via SIP trace as the real cause of queued calls
            // dying at ~61s: JsSIP rejects an unanswered incoming INVITE
            // after this many seconds by sending 408 Request Timeout —
            // default is 60. FreeSWITCH then logs it as
            // RECOVERY_ON_TIMER_EXPIRE, which misleadingly looks like a
            // SIP session timer (RFC 4028) issue; it isn't — FreeSWITCH's
            // own INVITE carries no Session-Expires header at all.
            // Raised to 600 so it comfortably outlasts the queue's own
            // 5-minute total_ring_time cap, which should always be what
            // actually ends a waiting call.
            no_answer_timeout: 600,
        });
        ua.on("registered", () => setReg("ok"));
        ua.on("unregistered", () => setReg("gone"));
        ua.on("registrationFailed", (e: any) => {
            console.error("SIP reg failed", e);
            setReg("fail");
        });
        ua.on("newRTCSession", ({ session }: any) => {
            sessRef.current = session;
            wireSession(session);
            if (session.direction === "incoming") {
                setRemoteId(session.remote_identity?.uri?.user || "Unknown");
                setCall("in");
                startTone("inbound"); // audible ring, not just a visual one
            }
        });
        ua.start();
        uaRef.current = ua;
        connectWs(); // one persistent connection for the whole session, not per-call
        return () => {
            if (toneStopRef.current) toneStopRef.current(); // stop any ring/ringback still playing
            toneStopRef.current = null;
            if (audioCtxRef.current) {
                audioCtxRef.current.close().catch(() => {});
                audioCtxRef.current = null;
            }
            if (timerRef.current) clearInterval(timerRef.current);
            if (dialTimeoutRef.current) clearTimeout(dialTimeoutRef.current);
            if (wsReconnectTimeoutRef.current) clearTimeout(wsReconnectTimeoutRef.current);
            if (wsRef.current) {
                wsRef.current.onclose = null; // don't trigger a reconnect on a deliberate unmount close
                wsRef.current.close();
            }
            ua.stop();
        };
    }, [sipUser, sipPass, sipDomain, wssUri, wireSession, connectWs, startTone]);

    const dial = (target?: string) => {
        const t = (target || dialValue).trim();
        if (!t || !uaRef.current || reg !== "ok") return;
        uaRef.current.call(`sip:${t}@${sipDomain}`, {
            mediaConstraints: { audio: true, video: false },
            pcConfig: { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] },
        });
        setRemoteId(t);
        setCall("out");
    };
    const hangup = () => {
        // This doubles as "leave the call" once a call has been promoted
        // into a multi-party conference: it only ends YOUR OWN SIP session.
        // FreeSWITCH conferences keep running as long as anyone's still in
        // them, so whoever else is on the call stays connected — no separate
        // "Leave" action needed.
        console.log("[HANG] sessRef=", sessRef.current, "callState=", call);
        if (!sessRef.current) {
            console.warn("[HANG] no session");
            return;
        }
        sessRef.current.terminate();
    };

    // Auto-ending the call when everyone else has left is handled entirely
    // server-side now (see server.js's conference loop) — it kicks the
    // last remaining member directly, which produces a real SIP BYE that
    // the existing sess.on("ended") handler above already reacts to. No
    // client-side "am I alone" polling or effect is needed anymore.
    const [lastDtmf, setLastDtmf] = React.useState("");
    const sendDTMF = (digit: string) => {
        if (!sessRef.current) return;
        try {
            sessRef.current.sendDTMF(digit, { transportType: "INFO", duration: 160, interToneGap: 50 });
            setLastDtmf(digit);
            setTimeout(() => setLastDtmf(""), 600);
            console.log("[DTMF] sent:", digit);
        } catch (e: any) {
            console.error("[DTMF] error:", e.message);
        }
    };
    const answer = () => {
        stopTone(); // cut the ring immediately on click, don't wait for 'confirmed'
        sessRef.current?.answer({
            mediaConstraints: { audio: true, video: false },
            pcConfig: { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] },
        });
    };
    const reject = () => {
        stopTone();
        sessRef.current?.terminate({ status_code: 486, reason_phrase: "Busy Here" });
    };

    const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

    const s: Record<string, React.CSSProperties> = {
        wrap: { position: "fixed", bottom: 20, right: 20, background: "#1a1a2e", color: "#fff", padding: 16, borderRadius: 12, width: 280, fontFamily: "monospace", fontSize: 13, zIndex: 9999, boxShadow: "0 8px 32px rgba(0,0,0,.5)" },
        row: { display: "flex", gap: 8, marginBottom: 8 },
        input: { flex: 1, padding: "6px 10px", borderRadius: 6, border: "1px solid #444", background: "#111", color: "#fff", fontSize: 13 },
        btn: { padding: "6px 12px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600 },
        didBtn: { flex: 1, padding: "6px 8px", borderRadius: 6, border: "1px solid #333", background: "#111", color: "#aaa", cursor: "pointer", fontSize: 11, textAlign: "left" as const },
        status: { fontSize: 11, color: reg === "ok" ? "#3ecf8e" : reg === "fail" ? "#ff4d6a" : "#f59e0b", marginBottom: 8 },
        divider: { borderTop: "1px solid #333", margin: "8px 0", fontSize: 10, color: "#555", paddingTop: 6 },
        confRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", fontSize: 11 },
        smallBtn: { padding: "3px 6px", borderRadius: 4, border: "none", cursor: "pointer", fontSize: 10, fontWeight: 600, marginLeft: 4 },
    };

    return (
        <div style={s.wrap}>
            <audio ref={audioRef} autoPlay />
            <div style={s.status}>
                ● {reg === "ok" ? "Registered" : reg === "fail" ? "Registration failed" : reg === "gone" ? "Disconnected" : "Connecting…"}
                {" · "}
                {sipUser}@{sipDomain}
            </div>

            {call === "idle" && (
                <>
                    {/* DID quick-dial */}
                    <div style={s.divider}>Quick dial</div>
                    {DIDS.map((d) => (
                        <div key={d.number} style={{ ...s.row, marginBottom: 6 }}>
                            <button style={s.didBtn} onClick={() => dial(d.number)} disabled={reg !== "ok"}>
                                <span style={{ color: "#fff" }}>{d.label}</span>
                                <br />
                                <span>{d.number}</span>
                            </button>
                            <button style={{ ...s.btn, background: "#3ecf8e", color: "#fff" }} onClick={() => dial(d.number)} disabled={reg !== "ok"}>
                                Call
                            </button>
                        </div>
                    ))}

                    {/* Free-type input */}
                    <div style={s.divider}>Or type a number</div>
                    <div style={s.row}>
                        <input style={s.input} value={dialValue} onChange={(e) => setDialValue(e.target.value)} placeholder="+61..." onKeyDown={(e) => e.key === "Enter" && dial()} />
                        <button style={{ ...s.btn, background: "#3ecf8e", color: "#fff" }} onClick={() => dial()} disabled={!dialValue || reg !== "ok"}>
                            Call
                        </button>
                    </div>
                </>
            )}

            {call === "in" && (
                <div>
                    <div style={{ marginBottom: 8 }}>📞 Incoming: {remoteId}</div>
                    <div style={s.row}>
                        <button style={{ ...s.btn, background: "#3ecf8e", color: "#fff", flex: 1 }} onClick={answer}>
                            Answer
                        </button>
                        <button style={{ ...s.btn, background: "#ff4d6a", color: "#fff", flex: 1 }} onClick={reject}>
                            Reject
                        </button>
                    </div>
                </div>
            )}

            {call === "out" && (
                <div>
                    <div style={{ marginBottom: 8 }}>Calling {remoteId}…</div>
                    <button style={{ ...s.btn, background: "#ff4d6a", color: "#fff", width: "100%" }} onClick={hangup}>
                        Cancel
                    </button>
                </div>
            )}

            {call === "active" && (
                <div>
                    <div style={{ marginBottom: 8, color: "#3ecf8e" }}>
                        ● On call: {remoteId} · {fmt(secs)}
                    </div>

                    {/* DTMF keypad */}
                    <div style={{ textAlign: "center", fontSize: 22, minHeight: 32, marginBottom: 6, letterSpacing: 4, color: "#3ecf8e" }}>{lastDtmf || " "}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: 10 }}>
                        {["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map((d) => (
                            <button key={d} style={{ ...s.btn, background: "#1e1e3a", color: "#fff", padding: "12px 0", fontSize: 18 }} onClick={() => sendDTMF(d)}>
                                {d}
                            </button>
                        ))}
                    </div>

                    {/* ── Conference controls ───────────────────────────── */}
                    <div style={s.divider}>Conference</div>

                    {confError && <div style={{ color: "#ff4d6a", fontSize: 10, marginBottom: 6 }}>{confError}</div>}
                    {dialingNumber && <div style={{ color: "#f59e0b", fontSize: 10, marginBottom: 6 }}>● Dialing {dialingNumber}…</div>}

                    <div style={s.row}>
                        <input style={s.input} value={addNumber} onChange={(e) => setAddNumber(e.target.value)} placeholder="Add caller +61..." onKeyDown={(e) => e.key === "Enter" && handleAddParticipant()} disabled={!!dialingNumber} />
                        <button style={{ ...s.btn, background: dialingNumber ? "#444" : "#3ecf8e", color: "#fff" }} onClick={handleAddParticipant} disabled={!addNumber || confBusy || !!dialingNumber}>
                            {dialingNumber ? "Dialing…" : "Add"}
                        </button>
                    </div>

                    <div style={s.row}>
                        <button
                            style={{ ...s.btn, background: "#1e1e3a", color: "#fff", flex: 1 }}
                            onClick={() => {
                                const next = !showParticipants;
                                setShowParticipants(next);
                                if (next) loadParticipants();
                            }}>
                            {showParticipants ? "Hide" : "Show"} participants {participants.length ? `(${participants.length})` : ""}
                        </button>
                        <button style={{ ...s.btn, background: recording ? "#ff4d6a" : "#1e1e3a", color: "#fff", flex: 1 }} onClick={handleToggleRecording} disabled={confBusy}>
                            {recording ? "⏹ Stop rec" : "⏺ Record"}
                        </button>
                    </div>

                    {showParticipants && (
                        <div style={{ background: "#111", borderRadius: 6, padding: 6, marginBottom: 8 }}>
                            {participants.length === 0 && <div style={{ fontSize: 10, color: "#666" }}>No participant data yet.</div>}
                            {participants.map((p) => (
                                <div key={p.id} style={s.confRow}>
                                    <span>{p.is_self ? "You" : p.caller_id_number || p.caller_id_name || p.id}</span>
                                    <span>
                                        <button style={{ ...s.smallBtn, background: "#444", color: "#fff" }} onClick={() => handleMemberAction(p.muted ? "/unmute" : "/mute", p.id)} disabled={confBusy}>
                                            {p.muted ? "Unmute" : "Mute"}
                                        </button>
                                        <button style={{ ...s.smallBtn, background: "#444", color: "#fff" }} onClick={() => handleMemberAction(p.can_hear === false ? "/unhold" : "/hold", p.id)} disabled={confBusy}>
                                            {p.can_hear === false ? "Unhold" : "Hold"}
                                        </button>
                                        <button style={{ ...s.smallBtn, background: "#ff4d6a", color: "#fff" }} onClick={() => handleMemberAction("/kick", p.id)} disabled={confBusy}>
                                            Remove
                                        </button>
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    <button style={{ ...s.btn, background: "#ff4d6a", color: "#fff", width: "100%", padding: "10px 0", fontSize: 14 }} onClick={hangup}>
                        ✕ Hang Up / Leave
                    </button>
                </div>
            )}
        </div>
    );
};

// ─── Dashboard ─────────────────────────────────────────────────────────────
const Dashboard: React.FC = () => {
    const [ready, setReady] = useState(false);
    const [sipCredentials, setSipCreds] = useState<SipCredentials | null>(null);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            // 1 — Auth check
            const auth = await new fetch(null, {}, { success: false, error: true }).auth();
            if (!auth || auth.status !== "success") {
                window.location.href = "/login";
                return;
            }

            // 2 — SIP credentials
            // Tokens live in cookies on the dashboard domain — pass them
            // explicitly in the body so they cross the domain boundary.
            const cookie = new Cookies();
            const sip = await new fetch("https://siptestea.ypc.solutions/user/vca/sip/credentials", { Authorization: cookie.get("token"), xAccessToken: cookie.get("access_token") }, { success: false, error: false }).post();
            if (sip?.status === "success") {
                setSipCreds(sip.data);
            } else {
                setError(`SIP credentials failed: ${JSON.stringify(sip)}`);
            }

            setReady(true);
        })();
    }, []);

    if (!ready) return <div style={{ padding: 20, fontFamily: "monospace" }}>Loading…</div>;

    return (
        <div style={{ padding: 20 }}>
            <h2>SIP Test</h2>
            {error && <pre style={{ color: "red" }}>{error}</pre>}
            {sipCredentials ? <WebPhone credentials={sipCredentials} /> : <p>No SIP credentials — check the server logs and SIP_USERS env var.</p>}
        </div>
    );
};

export default Dashboard;
